/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import { useDispatch, useSelector } from "react-redux";
import { selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { getCodeSystemAndVotesDict } from "../functions/getFhirResourceAndMetadataAPI";
import { useNavigate } from "react-router-dom";
import { Table } from "semantic-ui-react";
import { selectCurrentUserApiToken } from "../slices/currentUser/currentUserSlice";
import scrollTo from "../functions/scrollTo";
import navigateToAnotherTerm from "./codeSystemResourceFunctions/navigateToAnotherTerm";
import { displayStringFromFHIRCoding } from "../datatypeComponents/CodingEntry";
import { getDateTimeDisplay } from "../datatypeComponents/PeriodEntry";
import loadCodeSystemTermDetailComments from "./codeSystemResourceFunctions/loadCodeSystemTermDetailComments";
import {
    changeConceptCode, changeConceptCommentsExpanded,
    changeConceptJsonExpanded, changeConceptMyVotesExpanded, changeConceptName,
    changeConceptOpenForVoting,
    changeConceptPath, changeConceptPathIndexes, changeConceptPathString,
    changeConceptSelected, changeConceptVotesExpanded, changeNewChildConcepts,
    changeTermComments, changeTermMyVotes, changeTermVotes,
    selectConceptCode, selectConceptCommentsExpanded, selectConceptJsonExpanded,
    selectConceptMyVotesExpanded, selectConceptName, selectConceptPath, selectConceptPathIndexes,
    selectConceptPathString, selectConceptSelected,
    selectConceptVotesExpanded, selectHasApprovalProperty, selectTermComments
} from "../slices/codeSystemTermDetail/codeSystemTermDetailSlice";
import getPropertyCodes from "./codeSystemResourceFunctions/getPropertyCodes";
import findConceptProperties from "./codeSystemResourceFunctions/findConceptProperties";

const ChildConceptsTable = ({ resourceId }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, resourceId));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const conceptSelected = useSelector(selectConceptSelected);
    const conceptPath = useSelector(selectConceptPath);
    const conceptPathString = useSelector(selectConceptPathString);
    const conceptPathIndexes = useSelector(selectConceptPathIndexes);

    const [headerState, setHeaderState] = useState("");
    const [conceptState, setConceptState] = useState({});

    useEffect(() => {
        let header = "Child concepts:";
        let concept;
        if (conceptSelected) {
            concept = JSON.parse(JSON.stringify(conceptSelected));
        }
        if (conceptPath === undefined || conceptPath.length === 0) {
            concept = resourceJson;
            header = "Top-level concepts:"
        }
        setConceptState(concept);
        setHeaderState(header);
    }, [conceptSelected]);

    return <>
        {(conceptState?.concept?.length > 0) &&
            <>
                <b>{headerState}</b>
                {(conceptState?.concept?.length > 0) &&
                    <div>
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Code</Table.HeaderCell>
                                    <Table.HeaderCell>Display</Table.HeaderCell>
                                    <Table.HeaderCell>Definition</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {conceptState?.concept?.map((subConcept, subConceptIndex) => {
                                    let subConceptPath = [];
                                    let subConceptPathString = conceptPathString + ": " + subConcept.display;
                                    if (conceptPath) {
                                        subConceptPath = [...conceptPath, subConcept.display];
                                    } else {
                                        subConceptPath = [subConcept.display];
                                    }
                                    let subConceptPathIndexes = [];
                                    if (conceptPathIndexes) {
                                        subConceptPathIndexes = [...conceptPathIndexes, subConceptIndex];
                                    } else {
                                        subConceptPathIndexes = [subConceptIndex];
                                    }
                                    return <Table.Row key={"sub" + subConceptIndex}>
                                        <Table.Cell>{subConcept.code}</Table.Cell>
                                        <Table.Cell>
                                            <a
                                                href={`/resources/CodeSystem/${resourceId}#${subConcept.code}`}
                                                onClick={() => {
                                                    navigateToAnotherTerm(dispatch, resourceId,
                                                        subConcept, subConcept.display, subConceptPath,
                                                        subConceptPathIndexes, subConceptPathString, navigate);
                                                }}>
                                                {subConcept.display}
                                            </a>
                                        </Table.Cell>
                                        <Table.Cell>{subConcept.definition}</Table.Cell>
                                    </Table.Row>
                                })}
                            </Table.Body>
                        </Table>
                    </div>
                }
            </>
        }
    </>
};

const createPropertyCodeLookup = (resourceJson) => {
    let propertyCodeLookup = {};
    if (Array.isArray(resourceJson.property)) {
        for (const property of resourceJson.property) {
            if (property.code) {
                propertyCodeLookup[property.code] = property.description || property.code;
            }
        }
    }
    return propertyCodeLookup;
}

const CodeSystemTermDisplay = ({ resourceId }) => {

    const parentResourceDetail = useSelector(state => selectResourceById(state, resourceId));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const apiToken = useSelector(selectCurrentUserApiToken);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const concept = useSelector(selectConceptSelected);
    const conceptCode = useSelector(selectConceptCode);
    const conceptPath = useSelector(selectConceptPath);
    const conceptPathIndexes = useSelector(selectConceptPathIndexes);
    const hasApprovalProperty = useSelector(selectHasApprovalProperty);
    const termComments = useSelector(selectTermComments);
    const conceptName = useSelector(selectConceptName);
    const conceptCommentsExpanded = useSelector(selectConceptCommentsExpanded);
    const conceptVotesExpanded = useSelector(selectConceptVotesExpanded);
    const conceptMyVotesExpanded = useSelector(selectConceptMyVotesExpanded);
    const conceptJsonExpanded = useSelector(selectConceptJsonExpanded);

    const propertyCodeLookup = createPropertyCodeLookup(resourceJson);

    let showAsDraft = false;
    let conceptPathLength = 0;
    if (conceptPath) {
        conceptPathLength = conceptPath.length;
    }

    let alternativeTerms = [];
    if (concept?.designation?.length > 0) {
        for (const designation of concept.designation) {
            if (designation.use.code === "900000000000013009" || designation.use.display === "Alternative term") {
                if (designation.language) {
                    alternativeTerms.push(designation.value + " [" + designation.language + "]");
                } else {
                    alternativeTerms.push(designation.value);
                }
            }
            if (designation.use.code === "900000000000003001" && designation.language) {
                alternativeTerms.push(designation.value + " [Language: " + designation.language + "]");
            }
        }
    }

    if (hasApprovalProperty && concept) {
        let propertyProperties = getPropertyCodes(resourceJson);
        let propertyCodes = propertyProperties["propertyCodes"];
        let conceptProperties = findConceptProperties(concept, propertyCodes);
        if (!conceptProperties || !conceptProperties["approval"]) {
            showAsDraft = true;
        }
    }

    const [codeSystemAndVotesDictState, setCodeSystemAndVotesDictState] = useState({});
    //codeSystemAndVotesDictState is an object with fhirjsonstring, title, resourcetype, status, found, 
    // loggedin, readpermission, editpermission, adminpermission, 
    // commentsdict, votesdict, myvotesdict
    const [loadingState, setLoadingState] = useState(true);
    const [votesCountDisplayState, setVotesCountDisplayState] = useState("");
    const [voterSummaryState, setVoterSummaryState] = useState("");
    const [finalVotesState, setFinalVotesState] = useState([]);
    const [currentVotesState, setCurrentVotesState] = useState([]);
    const [openForVotingDateState, setOpenForVotingDateState] = useState();  //default value here is undefined, that's different behavior than if it were null. Thankfully the if statements that use openForVotingDateState check to see if it has a value first (i.e. not undefined, not null, no 0)
    //const [duplicateVotesState, setDuplicateVotesState] = useState([]);
    //const [oldVotesState, setOldVotesState] = useState([]);

    const loadCodeSystemAndVotesDict = async () => {
        let startingCodeSystemAndVotesDict = await getCodeSystemAndVotesDict(resourceId, apiToken);
        setCodeSystemAndVotesDictState(startingCodeSystemAndVotesDict);
        setLoadingState(false);
    };

    useEffect(() => {
        if (loadingState) {
            loadCodeSystemAndVotesDict();
        }
    }, [loadingState]);

    useEffect((() => {

        if (termComments &&
            codeSystemAndVotesDictState?.commentsdict?.[conceptCode] &&
            termComments.length !==
            codeSystemAndVotesDictState.commentsdict[conceptCode].length) {
            let commentFois = codeSystemAndVotesDictState.commentsdict[conceptCode];
            loadCodeSystemTermDetailComments(apiToken, commentFois, dispatch);
        }

        let voterSummary = "";
        let votesCountDisplay;
        let voutesCount = 0;
        //let usersLatestVotes = [];
        let finalVotes = [];
        let currentVotes = [];
        let notCountedVotes = [];
        let oldVotes = [];
        let duplicateVoterVotes = [];
        let currentPositiveVotes = [];
        let currentNegativeVotes = [];
        let usersVoted = [];
        let voteAuthorNames = [];

        let openForVotingDate;

        let propertyProperties = getPropertyCodes(resourceJson);
        let propertyCodes = propertyProperties["propertyCodes"];

        if (concept) {
            let conceptProperties = findConceptProperties(concept, propertyCodes);
            let conceptOpenForVoting = conceptProperties["open-for-voting"] ? true : false;
            dispatch(changeConceptOpenForVoting(conceptOpenForVoting));
            let openForVotingDateString = conceptProperties["open-for-voting"];
            if (openForVotingDateString) {
                openForVotingDate = new Date(openForVotingDateString);
                setOpenForVotingDateState(openForVotingDate);
            }
        }

        if (codeSystemAndVotesDictState?.editpermission && codeSystemAndVotesDictState.votesdict?.[conceptCode]) {
            voutesCount = codeSystemAndVotesDictState.votesdict[conceptCode].length;
            votesCountDisplay = voutesCount.toString();
            if (openForVotingDate) {
                for (let vote of codeSystemAndVotesDictState.votesdict[conceptCode]) {
                    let voteJsonString = JSON.stringify(vote);
                    let voteTime = new Date(vote.time);
                    if (voteTime >= openForVotingDate && vote.status !== "retired") {
                        if (!vote.userid || !usersVoted.includes(vote.userid)) {
                            voteAuthorNames.push(vote.author);
                            usersVoted.push(vote.userid);
                            currentVotes.push(voteJsonString);
                            //usersLatestVotes.push(voteJsonString);
                            if (vote.vote !== undefined) {
                                if (vote.vote) {
                                    currentPositiveVotes.push(voteJsonString);
                                } else {
                                    currentNegativeVotes.push(voteJsonString);
                                }
                            }
                        } else {
                            duplicateVoterVotes.push(voteJsonString);
                            notCountedVotes.push(voteJsonString);
                        }
                    } else {
                        oldVotes.push(voteJsonString);
                        notCountedVotes.push(voteJsonString);
                    }
                }
                if (currentVotes.length > 0 || notCountedVotes.length > 0) {
                    votesCountDisplay += ": ";
                }
                let addingOn = "";
                if (currentVotes.length > 0) {
                    let yesVotesCount = currentPositiveVotes.length.toString();
                    let noVotesCount = currentNegativeVotes.length.toString();
                    addingOn += yesVotesCount + " yes votes";
                    if (currentNegativeVotes) {
                        addingOn += ", " + noVotesCount + " no votes";
                    }
                    voterSummary = "vote " + yesVotesCount + "-" + noVotesCount + " by " + voteAuthorNames.join(", ");
                }
                if (duplicateVoterVotes.length > 0) {
                    if (addingOn) {
                        addingOn += ", ";
                    }
                    addingOn += duplicateVoterVotes.length.toString() + " multiple votes";
                }
                if (oldVotes.length > 0) {
                    if (addingOn) {
                        addingOn += ", ";
                    }
                    addingOn += oldVotes.length.toString() + " old votes";
                }
                votesCountDisplay += addingOn;
            } else {
                for (let vote of codeSystemAndVotesDictState.votesdict[conceptCode]) {
                    //usersLatestVotes.push();
                    if (!vote.userid || !usersVoted.includes(vote.userid)) {
                        let voteJsonString = JSON.stringify(vote);
                        usersVoted.push(vote.userid);
                        finalVotes.push(voteJsonString);
                    }
                }
                setFinalVotesState(finalVotes);
            }
        }
        setCurrentVotesState(currentVotes);
        //setDuplicateVotesState(duplicateVoterVotes);
        //setOldVotesState(oldVotes);
        setVotesCountDisplayState(votesCountDisplay || "0");
        setVoterSummaryState(voterSummary);
    }), [codeSystemAndVotesDictState, conceptCode, concept, termComments]);

    return <div>
        <div style={{ marginTop: "12px" }}>
            {conceptPath?.length > 0 ?
                <>
                    <h2 style={{ margin: "0px" }}>
                        {conceptName}
                        {showAsDraft && <span style={{ fontWeight: "normal", fontSize: "18px" }}> (draft)</span>}
                    </h2>
                    <br />
                    <span style={{ whiteSpace: "pre-wrap" }}>
                        {concept.code && <><b>Code:</b> {concept.code}<br /><br /></>}
                        {concept.display && <><b>Preferred term:</b> {concept.display}<br /><br /></>}
                        {concept.definition && <><b>Definition:</b> {concept.definition}<br /><br /></>}
                        {alternativeTerms.length > 0 && <>
                            <b>Alternative term{alternativeTerms.length > 1 && <>s</>}: </b>
                            <ul style={{ margin: "16px" }}>
                                {alternativeTerms.map((alternativeTerm, alternativeTermIndex) => {
                                    return <li key={"alt" + alternativeTermIndex}>{alternativeTerm}</li>
                                })}
                            </ul>
                            <br />
                        </>
                        }
                        {
                            concept.property?.length > 0 &&
                            concept.property.map((property, propIndex) => {
                                if (property.code === "comment") {
                                    return <div key={"comment" + propIndex}>
                                        <span><b>Comment for application: </b></span>
                                        <DisplayFromFHIRMarkdown markdown={property.valueString} />
                                        <br /><br />
                                    </div>;
                                } else {
                                    return <span key={propIndex}></span>;
                                }
                            })}
                        <>
                            {conceptCode ?
                                <span onClick={() => {
                                    dispatch(changeConceptName(""));
                                    dispatch(changeConceptCode(""));
                                    dispatch(changeConceptPath(undefined));
                                    dispatch(changeConceptPathIndexes(undefined));
                                    dispatch(changeConceptPathString(undefined));
                                    dispatch(changeConceptSelected(undefined));
                                    dispatch(changeNewChildConcepts([]));
                                    dispatch(changeTermComments([]));
                                    dispatch(changeTermVotes([]));
                                    dispatch(changeTermMyVotes([]));
                                    dispatch(changeConceptCommentsExpanded(false));
                                    scrollTo("navigationTermDetailSegment", "", true);
                                    navigate(`/resources/CodeSystem/${resourceId}#TOP`); //We need the replace unless we have back/forward go to the previous terms //, { replace: true }
                                }}>
                                    <b>TOP</b>
                                </span>
                                :
                                <b>TOP</b>
                            }
                            <br />
                            <span style={{ paddingLeft: "8px" }}>
                                {conceptPath?.map((parentConceptDisplay, parentConceptIndex) => {
                                    //let parentConceptPath = conceptPath.slice(0, parentConceptIndex + 1);
                                    let parentConceptPathIndexes = conceptPathIndexes.slice(0, parentConceptIndex + 1);
                                    let parentConcept = resourceJson;
                                    for (let parentIndexIndex in parentConceptPathIndexes) {
                                        let parentIndex = parentConceptPathIndexes[parentIndexIndex];
                                        parentConcept = parentConcept.concept[parentIndex];
                                    }
                                    //let parentConceptPathString = conceptPath.join(separator);
                                    return <span key={parentConceptIndex} style={{ paddingLeft: parentConceptIndex * 24 + "px" }}><span>
                                        {parentConceptIndex + 1 === conceptPath.length ?
                                            <b>{parentConceptDisplay}</b>
                                            :
                                            <a href={`/resources/CodeSystem/${resourceId}#${parentConcept.code}`}>
                                                {parentConceptDisplay}
                                            </a>
                                        }
                                    </span><br /></span>
                                })
                                }</span>
                        </>
                        {concept.concept?.length > 0 &&
                            <div>{concept.concept.map((subConcept, subConceptIndex) => {
                                return <span key={"subsub" + subConceptIndex}
                                    style={{ paddingLeft: conceptPathLength * 24 + "px" }}>
                                    <a href={`/resources/CodeSystem/${resourceId}#${subConcept.code}`}>
                                        {subConcept.display}
                                    </a>
                                    <br />
                                </span>
                            })
                            }
                            </div>
                        }
                        <br />
                        {
                            concept.property?.length > 0 &&
                            concept.property.map((property, propertyIndex) => {
                                if (property.code === "comment") {
                                    return <span key={propertyIndex}></span>;
                                } else {
                                    return <div key={"property" + propertyIndex}>
                                        <span><b>{propertyCodeLookup[property.code]}: </b></span>
                                        {property.valueString && property.valueString}
                                        {property.valueCode && property.valueCode}
                                        {property.valueInteger && property.valueInteger}
                                        {property.valueDecimal && property.valueDecimal}
                                        {property.valueBoolean === true && <>TRUE</>}
                                        {property.valueBoolean === false && <>FALSE</>}
                                        {property.valueCoding && displayStringFromFHIRCoding(property.valueCoding)}
                                        {property.valueDateTime && getDateTimeDisplay(property.valueDateTime)}
                                        <br /><br />
                                    </div>;
                                }
                            })}
                        <br />
                    </span>
                    <ChildConceptsTable resourceId={resourceId} />
                    <br />
                    {apiToken &&
                        <>
                            <span>
                                <b>Comments ({(codeSystemAndVotesDictState?.commentsdict &&
                                    codeSystemAndVotesDictState.commentsdict[conceptCode])
                                    ?
                                    codeSystemAndVotesDictState.commentsdict[conceptCode].length : 0})</b>
                                <span className={"unselectable"} style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        dispatch(changeConceptCommentsExpanded(!conceptCommentsExpanded));
                                    }}>
                                    {conceptCommentsExpanded ? <>▼</> : <>►</>}
                                </span>
                            </span>
                            {(conceptCommentsExpanded === true &&
                                codeSystemAndVotesDictState?.commentsdict?.[conceptCode] !== undefined) &&
                                <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                                    {termComments.length !== codeSystemAndVotesDictState.commentsdict[conceptCode].length
                                        ?
                                        <i>Loading...</i>
                                        :
                                        termComments.map((comment, commentIndex) => {
                                            return <div key={"comment" + commentIndex}><div style={{ padding: "12px", paddingLeft: "0px" }}>
                                                {comment &&
                                                    <div style={{ border: "1px solid", padding: "10px", backgroundColor: "#FFFFFF" }}>
                                                        {comment.status !== "retired" ?
                                                            <><b>{comment.author}: </b>{comment.summary}<br /><i>{comment.time}</i></>
                                                            :
                                                            <b>RETIRED COMMENT</b>
                                                        }
                                                    </div>
                                                }
                                            </div></div>
                                        })}
                                </div>}
                            <br /><br />
                            {codeSystemAndVotesDictState?.editpermission &&
                                <>
                                    <span><b>Votes ({votesCountDisplayState})</b>
                                        <span className={"unselectable"} style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                dispatch(changeConceptVotesExpanded(!conceptVotesExpanded))
                                            }}>
                                            {conceptVotesExpanded ? <>▼</> : <>►</>}
                                        </span>
                                    </span>
                                    {(conceptVotesExpanded === true &&
                                        codeSystemAndVotesDictState.votesdict?.[conceptCode] !== undefined) &&
                                        <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                                            {voterSummaryState && <div style={{ "paddingTop": "6px" }}>
                                                {new Date().toISOString().substring(0, 10)}
                                                &nbsp;
                                                {voterSummaryState}
                                            </div>}
                                            {codeSystemAndVotesDictState.votesdict[conceptCode].map((vote, voteIndex) => {
                                                let backgroundColor = "#EAEAEA";
                                                let stringifiedVote = JSON.stringify(vote);
                                                if (currentVotesState.includes(stringifiedVote)) {
                                                    backgroundColor = "#FFEDB2";
                                                } else if (finalVotesState.includes(stringifiedVote)) {
                                                    //backgroundColor = "#EDB2FF";
                                                }
                                                return <div key={"vote" + voteIndex}><div style={{ padding: "12px", paddingLeft: "0px" }}>
                                                    {vote &&
                                                        <div style={{ border: "1px solid", padding: "10px", backgroundColor: backgroundColor }}>
                                                            {vote.status !== "retired" ?
                                                                <>
                                                                    {vote.vote !== undefined && <span><b>{vote.vote ? "Yes" : "No"}</b><br /></span>}
                                                                    {vote.summary && <span>{vote.summary}<br /></span>}
                                                                    <span>re: "{vote.termconcept?.display}" = "{vote.termconcept?.definition}"<br /></span>
                                                                    <span>By: {vote.author} (<i>{vote.time}</i>)<br /></span>
                                                                </>
                                                                :
                                                                <b>RETIRED VOTE</b>
                                                            }
                                                        </div>
                                                    }
                                                </div></div>
                                            })}</div>}
                                    <br /><br />
                                </>}
                            {(codeSystemAndVotesDictState?.myvotesdict?.[conceptCode]?.length > 0) &&
                                <>
                                    <span><b>My Votes ({(codeSystemAndVotesDictState.myvotesdict[conceptCode]) ?
                                        codeSystemAndVotesDictState.myvotesdict[conceptCode].length : 0})</b> <span
                                            className={"unselectable"} style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                dispatch(changeConceptMyVotesExpanded(!conceptMyVotesExpanded))
                                            }}>{conceptMyVotesExpanded ? <>▼</> : <>►</>}</span>
                                    </span>
                                    {(conceptMyVotesExpanded === true &&
                                        codeSystemAndVotesDictState.myvotesdict[conceptCode] !== undefined) &&
                                        <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                                            {codeSystemAndVotesDictState.myvotesdict[conceptCode]?.map((vote, voteIndex) => {
                                                let backgroundColor = "#EAEAEA";
                                                if (voteIndex === 0 && openForVotingDateState && new Date(vote.time) >= openForVotingDateState) {
                                                    backgroundColor = "#FFEDB2";
                                                }
                                                return <div key={"my" + voteIndex}><div style={{ padding: "12px", paddingLeft: "0px" }}>
                                                    {vote &&
                                                        <div style={{ border: "1px solid", padding: "10px", backgroundColor: backgroundColor }}>
                                                            {vote.status !== "retired" ?
                                                                <>
                                                                    {/*vote.classifierTexts?.length > 0 && <span><b>{vote.classifierTexts[0]}</b><br /></span>*/}
                                                                    {typeof (vote.vote) === "boolean" && <span><b>{vote.vote ? "Yes" : "No"}</b><br /></span>}
                                                                    {vote.summary && <span>{vote.summary}<br /></span>}
                                                                    {/*vote.componentDict["term voted upon"] && <span>re: {vote.componentDict["term voted upon"]}<br /></span>*/}
                                                                    {vote.termconcept &&
                                                                        <span>re: {`"${vote.termconcept.display}" = "${vote.termconcept.definition}"`}<br />
                                                                        </span>}
                                                                    <span>By: {vote.author} (<i>{vote.time}</i>)<br /></span>
                                                                </>
                                                                :
                                                                <b>RETIRED VOTE</b>
                                                            }
                                                        </div>
                                                    }
                                                </div></div>
                                            })}</div>}
                                    <br /><br />
                                </>}
                        </>
                    }
                    <span>
                        <b>JSON</b> <span className={"unselectable"} style={{ cursor: "pointer" }}
                            onClick={() => {
                                dispatch(changeConceptJsonExpanded(!conceptJsonExpanded))
                            }}>{conceptJsonExpanded ? <>▼</> : <>►</>}</span>
                    </span>
                    <br />
                    {conceptJsonExpanded === true &&
                        <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                            {JSON.stringify(concept, null, 2)}
                        </div>}
                </>
                :
                <>
                    <span className="instructionText">
                        <span>Please select a term</span>
                        <br />
                        <br />
                    </span>
                    <ChildConceptsTable resourceId={resourceId} />
                </>
            }
        </div>
    </div>
}

export default CodeSystemTermDisplay;